/**
 * Display for calendar dates held as `"YYYY-MM-DD"` strings.
 *
 * **No plain date becomes an instant here.** `new Date('2026-09-06')` parses as UTC midnight and
 * then renders in the device's zone, so a browser west of Greenwich shows the previous day — which
 * is what the live confirmation screen does today, on the screen that confirms a customer's
 * booking. Everything below builds its `Date` with `Date.UTC` and formats it with an explicit
 * `timeZone: 'UTC'`, so the zone never enters.
 *
 * **Kazakh is formatted here rather than by `Intl`, because Chrome cannot format it.** Chrome ships
 * a reduced ICU locale set with no `kk` calendar data at all, so
 * `Intl.DateTimeFormat('kk', { month: 'long' })` falls back to the root locale and answers `M01` —
 * and `weekday: 'short'` answers `Mon`. Every date on the site is affected: an article's byline, a
 * portal case's opening date, the booking calendar's month heading. Firefox and Safari carry the
 * full set and would render it correctly, which is why the defect survives a spot check on a
 * developer's machine. The names below are the site's own, so the two browsers now agree.
 */

/** Nominative, capitalised — the forms the live site's own pages use. */
const KAZAKH_MONTHS: readonly string[] = [
    'Қаңтар',
    'Ақпан',
    'Наурыз',
    'Сәуір',
    'Мамыр',
    'Маусым',
    'Шілде',
    'Тамыз',
    'Қыркүйек',
    'Қазан',
    'Қараша',
    'Желтоқсан'
];

/** Sunday first, so a `Date`'s own `getUTCDay()` indexes it directly. */
const KAZAKH_WEEKDAYS_SHORT: readonly string[] = ['Жк', 'Дс', 'Сс', 'Ср', 'Бс', 'Жм', 'Сб'];

const KAZAKH_WEEKDAYS_LONG: readonly string[] = [
    'Жексенбі',
    'Дүйсенбі',
    'Сейсенбі',
    'Сәрсенбі',
    'Бейсенбі',
    'Жұма',
    'Сенбі'
];

export function formatPlainDate(
    date: string,
    locale: string,
    options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long' }
): string {
    if (locale.startsWith('kk') && namesKazakh(options)) {
        return formatKazakh(date, options);
    }

    return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(new Date(utc(date)));
}

/** `"HH:mm"` from the `"HH:mm:ss"` a `TimeOnly` arrives as. */
export function toShortTime(time: string): string {
    return time.slice(0, 5);
}

/**
 * Whether the request needs a name Chrome has none of. A purely numeric date is digits and
 * separators, which the root fallback renders perfectly well — so it is left to `Intl`, and this
 * stays a substitute for missing data rather than a second date formatter.
 */
function namesKazakh(options: Intl.DateTimeFormatOptions): boolean {
    return options.weekday !== undefined || options.month === 'long' || options.month === 'short';
}

/**
 * Kazakh order is weekday, day, month, year — the same as Russian, and the same as the live site's
 * own `book.js` writes it.
 */
function formatKazakh(date: string, options: Intl.DateTimeFormatOptions): string {
    const [year, month, day] = date.split('-').map(Number);
    const weekday = new Date(utc(date)).getUTCDay();
    const parts: string[] = [];

    if (options.weekday === 'long') {
        parts.push(KAZAKH_WEEKDAYS_LONG[weekday]);
    } else if (options.weekday !== undefined) {
        parts.push(KAZAKH_WEEKDAYS_SHORT[weekday]);
    }

    if (options.day !== undefined) {
        parts.push(options.day === '2-digit' ? String(day).padStart(2, '0') : String(day));
    }

    if (options.month === 'short') {
        parts.push(KAZAKH_MONTHS[month - 1].slice(0, 3));
    } else if (options.month !== undefined) {
        parts.push(KAZAKH_MONTHS[month - 1]);
    }

    if (options.year !== undefined) {
        parts.push(String(year));
    }

    return parts.join(' ');
}

function utc(date: string): number {
    const [year, month, day] = date.split('-').map(Number);

    return Date.UTC(year, month - 1, day);
}
